'use client';

import React, { useCallback, useEffect, useRef, useState } from 'react';

interface EducationFocusComponentProps {
  componentId: string;
  initialEducationFocus?: Array<string>;
  onUpdate: (componentId: string, data: any) => void;
  onRemove: (componentId: string) => void;
  fromPlaceholder?: boolean;
}

/**
 * Free-text input pill for the `education_focus` criteria type.
 * The value is matched case-insensitively as a substring by the backend
 * (e.g. "cardiology" matches "Interventional Cardiology").
 */
const EducationFocusComponent: React.FC<EducationFocusComponentProps> = ({
  componentId,
  initialEducationFocus,
  onUpdate,
  onRemove,
  fromPlaceholder = false,
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const getInitialValue = (): string => {
    const value = initialEducationFocus?.[0];
    return typeof value === 'string' ? value : '';
  };

  const [committedValue, setCommittedValue] = useState<string>(
    getInitialValue()
  );
  const [draftValue, setDraftValue] = useState<string>(getInitialValue());

  // Track if this is a new component from a placeholder that hasn't been confirmed yet
  const [isNewFromPlaceholder, setIsNewFromPlaceholder] = useState(
    fromPlaceholder && !getInitialValue()
  );

  const updateRedux = useCallback(
    (focus: string) => {
      onUpdate(componentId, {
        type: 'education_focus',
        value: focus,
      });
    },
    [componentId, onUpdate]
  );

  // Auto-open input if starting with no value (new component from placeholder)
  useEffect(() => {
    if (!committedValue) {
      setIsOpen(true);
    } else {
      // Register existing value on mount
      updateRedux(committedValue);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run on mount

  // Focus the input when the editor opens
  useEffect(() => {
    if (isOpen) {
      inputRef.current?.focus();
    }
  }, [isOpen]);

  const handleCommit = useCallback(() => {
    const trimmed = draftValue.trim();

    if (!trimmed) {
      // No value entered — remove if never confirmed, otherwise keep previous value
      if (!committedValue) {
        onRemove(componentId);
        return;
      }
      setDraftValue(committedValue);
      setIsOpen(false);
      return;
    }

    setCommittedValue(trimmed);
    setDraftValue(trimmed);
    setIsOpen(false);
    setIsNewFromPlaceholder(false);
    updateRedux(trimmed);
  }, [draftValue, committedValue, componentId, onRemove, updateRedux]);

  const handleClose = useCallback(() => {
    setIsOpen(false);
    if (isNewFromPlaceholder && !committedValue) {
      onRemove(componentId);
      return;
    }
    setDraftValue(committedValue);
  }, [isNewFromPlaceholder, committedValue, onRemove, componentId]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        handleCommit();
      } else if (e.key === 'Escape') {
        e.preventDefault();
        handleClose();
      }
    },
    [handleCommit, handleClose]
  );

  const handleRemove = useCallback(() => {
    onRemove(componentId);
  }, [componentId, onRemove]);

  return (
    <span
      className="inline-flex items-center gap-1 text-rose-600 text-sm font-semibold group relative"
      data-testid="education-focus-component"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setIsOpen(!isOpen);
        }}
        className="inline-flex items-center gap-1 hover:text-rose-700 cursor-pointer"
        data-testid="education-focus-selector"
        aria-label="Enter education focus"
        aria-expanded={isOpen}
        aria-haspopup="dialog"
      >
        <span data-testid="education-focus-label">
          {committedValue || 'Enter focus...'}
        </span>
      </button>

      {/* Text input popover */}
      {isOpen && (
        <div
          className="absolute top-full left-0 mt-1 bg-white border border-neutral-200 rounded-lg shadow-lg z-50 p-2 flex items-center gap-2"
          role="dialog"
          aria-label="Education focus input"
        >
          <input
            ref={inputRef}
            type="text"
            value={draftValue}
            onChange={(e) => setDraftValue(e.target.value)}
            onKeyDown={handleKeyDown}
            onClick={(e) => e.stopPropagation()}
            placeholder="e.g. Cardiology"
            className="w-40 px-3 py-1 text-sm text-neutral-700 font-normal bg-white border border-neutral-300 rounded-full focus:outline-none focus:border-rose-400"
            aria-label="Education focus"
            data-testid="education-focus-input"
          />
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              handleCommit();
            }}
            className="px-3 py-1 text-xs font-medium text-white bg-rose-600 hover:bg-rose-700 rounded-full"
            aria-label="Apply education focus"
            data-testid="education-focus-apply-button"
          >
            Apply
          </button>
        </div>
      )}

      {/* Remove button */}
      <button
        className={`text-xs transition-all ${
          isHovered
            ? 'text-red-500 hover:text-red-700 opacity-100'
            : 'text-gray-400 opacity-0 w-0 group-hover:opacity-100 group-hover:w-auto'
        }`}
        onClick={(e) => {
          e.stopPropagation();
          handleRemove();
        }}
        aria-label="Remove education focus"
        data-testid="education-focus-remove-button"
      >
        x
      </button>
    </span>
  );
};

export default EducationFocusComponent;
